import { createFileRoute } from "@tanstack/react-router";
import * as React from "react";

import {
  clearControlSession,
  loadControlSession,
  storeControlSession,
  type ControlSession,
} from "../lib/control-session";
import { SidecarHttpClient, SidecarHttpError } from "../lib/sidecar-http-client";
import { SidecarWsClient } from "../lib/sidecar-ws-client";

export const Route = createFileRoute("/control")({
  component: ControlPage,
});

/**
 * Local, typing-only re-declarations of the wire shapes this page needs —
 * mirrors `apps/desktop`'s own `lib/timer/types.ts`/`lib/events/types.ts`
 * convention (never importing server packages directly), applied here for
 * the same reason `display.$outputId.tsx` already declares its own local
 * `OutputDto` instead of sharing one.
 */
type TimerStatus = "idle" | "running" | "paused" | "overtime" | "stopped";
type TimerType = "countdown" | "countup" | "clock" | "interval" | "session" | "break";

interface TimerState {
  readonly itemId: string | null;
  readonly type: TimerType;
  readonly status: TimerStatus;
  readonly durationMs: number;
  readonly anchorWallMs: number;
  readonly elapsedAtAnchorMs: number;
  readonly targetWallMs?: number;
  readonly warnAtMs: number;
  readonly criticalAtMs: number;
  readonly seq: number;
}

interface ScheduleItemDto {
  readonly id: string;
  readonly title: string;
  readonly speaker: string | null;
}

interface ActiveEventDto {
  readonly id: string;
  readonly name: string;
  readonly items: ScheduleItemDto[];
}

/** Mirrors `apps/desktop`'s `lib/timer/timer-math.ts`'s `computeRemainingMs` — same formula, same per-type/per-status branching. */
function computeRemainingMs(state: TimerState, skewMs: number, nowMs: number): number {
  const correctedNowMs = nowMs + skewMs;
  if (state.type === "break") return (state.targetWallMs ?? correctedNowMs) - correctedNowMs;
  if (state.type === "clock") return 0;
  if ((state.type === "countup" || state.type === "session") && state.durationMs <= 0) {
    return Number.POSITIVE_INFINITY;
  }
  const elapsedMs =
    state.status === "running" || state.status === "overtime"
      ? state.elapsedAtAnchorMs + (correctedNowMs - state.anchorWallMs)
      : state.elapsedAtAnchorMs;
  return state.durationMs - elapsedMs;
}

/**
 * Mirrors `apps/desktop`'s `lib/timer/timer-math.ts`'s `computeElapsedMs`
 * — how long the timer has actually been running. `computeRemainingMs`
 * reports `Infinity` for an untimed count-up/session (no target to count
 * down to), which `formatDuration` renders as a frozen "--:--"; a
 * count-up needs this instead, so the now-playing card actually counts.
 */
function computeElapsedMs(state: TimerState, skewMs: number, nowMs: number): number {
  const correctedNowMs = nowMs + skewMs;
  return state.status === "running" || state.status === "overtime"
    ? state.elapsedAtAnchorMs + (correctedNowMs - state.anchorWallMs)
    : state.elapsedAtAnchorMs;
}

/** Current wall-clock time — what a live `timerType: "clock"` item shows instead of a countdown (`computeRemainingMs` returns 0 for that type). */
function formatClockTime(nowMs: number): string {
  return new Date(nowMs).toLocaleTimeString(navigator.language, {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
}

/** Mirrors `apps/desktop`'s `lib/timer/timer-math.ts`'s `formatDuration`. */
function formatDuration(ms: number): string {
  if (!Number.isFinite(ms)) return "--:--";
  const negative = ms < 0;
  const totalSeconds = Math.floor(Math.abs(ms) / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  const body =
    hours > 0
      ? `${hours}:${minutes.toString().padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`
      : `${minutes}:${seconds.toString().padStart(2, "0")}`;
  return negative ? `-${body}` : body;
}

/**
 * Mirrors `apps/desktop`'s `lib/timer/countdown-color-tiers.ts` byte-for-byte
 * (same zero-import-path convention every other local re-declaration in this
 * file already follows) — this is what makes the mobile Controller's own
 * now-playing countdown change color on the same schedule as the real
 * Extended Display, instead of staying plain white regardless of how little
 * time is left on an item.
 */
interface CountdownColorTiers {
  readonly countdownTier1RedAtMs: number;
  readonly countdownTier2RedAtMs: number;
  readonly countdownTier3YellowAtMs: number;
  readonly countdownTier3RedAtMs: number;
  readonly countdownTier4YellowAtMs: number;
  readonly countdownTier4RedAtMs: number;
}

const DEFAULT_COUNTDOWN_COLOR_TIERS: CountdownColorTiers = {
  countdownTier1RedAtMs: 60_000,
  countdownTier2RedAtMs: 120_000,
  countdownTier3YellowAtMs: 300_000,
  countdownTier3RedAtMs: 120_000,
  countdownTier4YellowAtMs: 600_000,
  countdownTier4RedAtMs: 300_000,
};

type CountdownColorZone = "normal" | "warning" | "critical";

const COUNTDOWN_ZONE_COLOR: Record<CountdownColorZone, string | undefined> = {
  normal: undefined,
  warning: "#eab308",
  critical: "#ef4444",
};

const TIER_MIN_5 = 5 * 60_000;
const TIER_MIN_10 = 10 * 60_000;
const TIER_MIN_20 = 20 * 60_000;

/** Mirrors `apps/desktop`'s `lib/timer/countdown-color-tiers.ts`'s `resolveCountdownZone` — same tier boundaries, same fallback for sub-2-minute items. */
function resolveCountdownZone(
  durationMs: number,
  remainingMs: number,
  tiers: CountdownColorTiers,
): CountdownColorZone {
  if (durationMs < TIER_MIN_5) {
    return remainingMs <= tiers.countdownTier1RedAtMs ? "critical" : "normal";
  }
  if (durationMs < TIER_MIN_10) {
    return remainingMs <= tiers.countdownTier2RedAtMs ? "critical" : "normal";
  }
  if (durationMs < TIER_MIN_20) {
    if (remainingMs <= tiers.countdownTier3RedAtMs) return "critical";
    if (remainingMs <= tiers.countdownTier3YellowAtMs) return "warning";
    return "normal";
  }
  if (remainingMs <= tiers.countdownTier4RedAtMs) return "critical";
  if (remainingMs <= tiers.countdownTier4YellowAtMs) return "warning";
  return "normal";
}

type PrimaryAction = "start" | "pause" | "resume" | "stop" | "restart";

/** Mirrors `apps/desktop`'s `lib/timer/transport-action.ts`'s `resolvePrimaryTransportAction` — same state-machine mapping (pause only valid from `running`, resume only from `paused`, per `@stagvista/timer-engine`'s own `validateStateTransition`). */
function resolvePrimaryAction(status: TimerStatus | undefined): {
  label: string;
  action: PrimaryAction;
} {
  switch (status) {
    case "running":
      return { label: "Pause", action: "pause" };
    case "paused":
      return { label: "Resume", action: "resume" };
    case "overtime":
      return { label: "Stop", action: "stop" };
    case "stopped":
      return { label: "Restart", action: "restart" };
    case "idle":
    default:
      return { label: "Start", action: "start" };
  }
}

function canTransport(role: ControlSession["role"]): boolean {
  return role === "operator" || role === "assistant";
}

function problemMessage(cause: unknown, fallback: string): string {
  return cause instanceof SidecarHttpError
    ? (cause.problem.detail ?? cause.problem.title)
    : fallback;
}

/**
 * docs/design.md §5.12/Module 11 — the actual browser-rendered page a
 * phone/tablet loads after scanning the Remote Control screen's QR code
 * (or opening its embedded link/typing the PIN). Reached only via
 * `packages/local-server`'s `GET /control` redirect. A paired session
 * (operator/assistant/viewer, PIN-or-QR-gated, identical mechanism to
 * `display.$outputId.tsx`'s own pairing) is required before this page can
 * read or control anything; the controls it shows are gated by that
 * session's own role, matching `RoleAuthorizationService`'s capability
 * matrix exactly (`timer:read` all roles, `timer:transport`
 * operator+assistant, `outputs:blackout`/`message:send`/`message:clear`
 * operator only).
 */
function ControlPage(): React.ReactElement {
  const [session, setSession] = React.useState<ControlSession | null>(() => loadControlSession());

  const handleDisconnect = React.useCallback(() => {
    clearControlSession();
    setSession(null);
  }, []);

  const handleSessionUpdate = React.useCallback((updated: ControlSession) => {
    storeControlSession(updated);
    setSession(updated);
  }, []);

  if (!session) return <PairingGate onPaired={setSession} />;
  return (
    <ControllerView
      session={session}
      onDisconnect={handleDisconnect}
      onSessionUpdate={handleSessionUpdate}
    />
  );
}

function PairingGate({
  onPaired,
}: {
  onPaired: (session: ControlSession) => void;
}): React.ReactElement {
  const [qrToken] = React.useState(() => new URLSearchParams(window.location.search).get("token"));
  const [pin, setPin] = React.useState("");
  const [deviceName, setDeviceName] = React.useState("");
  const [error, setError] = React.useState<string | null>(null);
  const [phase, setPhase] = React.useState<"idle" | "submitting" | "waiting-for-approval">("idle");

  const acceptApproval = React.useCallback(
    (approval: { token: string; role: ControlSession["role"]; expiresAt: number }) => {
      const session: ControlSession = {
        role: approval.role,
        token: approval.token,
        expiresAt: approval.expiresAt,
      };
      storeControlSession(session);
      onPaired(session);
    },
    [onPaired],
  );

  const pollUntilResolved = React.useCallback(
    (pendingRequestId: string) => {
      setPhase("waiting-for-approval");
      const client = new SidecarHttpClient(() => null);

      const poll = async (): Promise<void> => {
        try {
          const result = await client.request<
            | { status: "pending" }
            | { status: "approved"; token: string; role: ControlSession["role"]; expiresAt: number }
            | { status: "rejected" }
            | { status: "expired" }
          >("GET", `/api/v1/pairing/pending/${pendingRequestId}/status`);
          if (result.status === "pending") {
            setTimeout(() => void poll(), 2000);
            return;
          }
          if (result.status === "approved") {
            acceptApproval(result);
            return;
          }
          setError(
            result.status === "rejected"
              ? "The operator declined this device's pairing request."
              : "This pairing request expired before it was approved — try again.",
          );
          setPhase("idle");
        } catch {
          setError("Lost contact with the local server while waiting for approval.");
          setPhase("idle");
        }
      };

      void poll();
    },
    [acceptApproval],
  );

  const submitConnect = React.useCallback(
    async (event: React.FormEvent) => {
      event.preventDefault();
      setPhase("submitting");
      setError(null);
      try {
        const client = new SidecarHttpClient(() => null);
        const trimmedName = deviceName.trim();
        const response = await client.request<
          | { status: "approved"; token: string; role: ControlSession["role"]; expiresAt: number }
          | { status: "pending"; pendingRequestId: string }
        >("POST", "/api/v1/pair", {
          ...(qrToken ? { qrRedemptionToken: qrToken } : { pin }),
          ...(trimmedName ? { deviceName: trimmedName } : {}),
        });
        if (response.status === "approved") acceptApproval(response);
        else pollUntilResolved(response.pendingRequestId);
      } catch (cause) {
        setError(problemMessage(cause, "Could not reach the local server."));
        setPhase("idle");
      }
    },
    [qrToken, pin, deviceName, acceptApproval, pollUntilResolved],
  );

  if (phase === "waiting-for-approval") {
    return (
      <main style={centeredPageStyle}>
        <img src="/images/taskbar-icon.png" alt="" style={logoIconStyle} />
        <h1 style={{ fontSize: 24, fontWeight: 600 }}>Waiting for approval…</h1>
        <p style={{ maxWidth: 320, textAlign: "center", opacity: 0.7 }}>
          An operator needs to approve this device from the Remote Control screen.
        </p>
        {error ? <p style={{ color: "#ef4444" }}>{error}</p> : null}
      </main>
    );
  }

  const canSubmit = qrToken
    ? deviceName.trim().length > 0
    : pin.length === 6 && deviceName.trim().length > 0;

  return (
    <main style={centeredPageStyle}>
      <img src="/images/taskbar-icon.png" alt="StagVista" style={logoIconStyle} />
      <form
        onSubmit={submitConnect}
        style={{ display: "flex", width: 288, flexDirection: "column", gap: 12 }}
      >
        <h1 style={{ fontSize: 24, fontWeight: 600, textAlign: "center" }}>Connect to StagVista</h1>
        {!qrToken ? (
          <input
            value={pin}
            onChange={(e) => setPin(e.target.value)}
            placeholder="6-digit PIN"
            maxLength={6}
            autoFocus
            style={pinInputStyle}
          />
        ) : null}
        <input
          value={deviceName}
          onChange={(e) => setDeviceName(e.target.value)}
          placeholder="Your name or device (e.g. Sarah's iPad)"
          maxLength={60}
          autoFocus={Boolean(qrToken)}
          style={pinInputStyle}
        />
        <p style={{ margin: 0, fontSize: 12, opacity: 0.6 }}>
          Shown to the operator approving/managing this device.
        </p>
        <button
          type="submit"
          disabled={!canSubmit || phase === "submitting"}
          style={primaryButtonStyle(!canSubmit || phase === "submitting")}
        >
          {phase === "submitting" ? "Connecting…" : "Connect"}
        </button>
        {error ? <p style={{ color: "#ef4444" }}>{error}</p> : null}
      </form>
    </main>
  );
}

function ControllerView({
  session,
  onDisconnect,
  onSessionUpdate,
}: {
  session: ControlSession;
  onDisconnect: () => void;
  onSessionUpdate: (session: ControlSession) => void;
}): React.ReactElement {
  const [event, setEvent] = React.useState<ActiveEventDto | null>(null);
  const [state, setState] = React.useState<TimerState | null>(null);
  const [blackout, setBlackout] = React.useState(false);
  const [extendedDisplayOpen, setExtendedDisplayOpen] = React.useState(false);
  const [currentMessage, setCurrentMessage] = React.useState<{
    text: string;
    flash: boolean;
  } | null>(null);
  const [now, setNow] = React.useState(() => Date.now());
  const [actionError, setActionError] = React.useState<string | null>(null);
  const [messageText, setMessageText] = React.useState("");
  const [messageFlash, setMessageFlash] = React.useState(false);
  const [isBusy, setIsBusy] = React.useState(false);
  const [countdownColorTiers, setCountdownColorTiers] = React.useState<CountdownColorTiers>(
    DEFAULT_COUNTDOWN_COLOR_TIERS,
  );

  const skewMsRef = React.useRef(0);
  const clientSeqRef = React.useRef(0);
  const reportKnownSeq = React.useCallback((seq: number) => {
    if (seq > clientSeqRef.current) clientSeqRef.current = seq;
  }, []);
  const nextClientSeq = React.useCallback(() => (clientSeqRef.current += 1), []);

  const http = React.useMemo(() => new SidecarHttpClient(() => session.token), [session.token]);

  const fetchEvent = React.useCallback(async () => {
    try {
      const active = await http.request<ActiveEventDto | undefined>("GET", "/api/v1/events/active");
      setEvent(active ?? null);
    } catch {
      // Transient — the next reconnect/WS-driven refetch retries naturally.
    }
  }, [http]);

  React.useEffect(() => {
    void fetchEvent();

    void http
      .request<{ timerId: string; state: TimerState }>("GET", "/api/v1/timer")
      .then((r) => {
        setState(r.state);
        reportKnownSeq(r.state.seq);
      })
      .catch(() => undefined);

    void http
      .request<Array<{ blackout: boolean }>>("GET", "/api/v1/outputs")
      .then((outputs) => setBlackout(outputs.some((o) => o.blackout)))
      .catch(() => undefined);

    // 403s for a viewer session (`timer:transport` only) — harmless, the
    // button this seeds just never renders for that role.
    void http
      .request<{ open: boolean }>("GET", "/api/v1/display/extended/status")
      .then((r) => setExtendedDisplayOpen(r.open))
      .catch(() => undefined);

    // Gated `timer:read` (every paired role, unlike `settings:manage`'s
    // operator-only `GET /api/v1/settings`) — see
    // `extended-display.route.ts`'s own doc comment on why this narrow
    // read got its own endpoint instead.
    void http
      .request<CountdownColorTiers>("GET", "/api/v1/display/extended/countdown-color-tiers")
      .then((tiers) => setCountdownColorTiers(tiers))
      .catch(() => undefined);

    // Seeds whatever message is already on screen — without this,
    // opening the Controller while a message is active shows nothing
    // until the operator sends a new one (`message:set`/`message:cleared`
    // below only ever fire on the *next* change).
    void http
      .request<{ message: { text: string; flash: boolean } | null }>("GET", "/api/v1/message")
      .then((r) => setCurrentMessage(r.message))
      .catch(() => undefined);

    const ws = new SidecarWsClient(
      () => session.token,
      ["timer", "outputs", "message", "schedule"],
    );
    ws.start();

    const unsubscribers = [
      ws.on("timer:state", (message) => {
        const nextState = message.state as unknown as TimerState;
        setState(nextState);
        reportKnownSeq(nextState.seq);
      }),
      ws.on("output:blackout", (message) => {
        if (message.outputId === "all") setBlackout(Boolean(message.on));
      }),
      // The Extended Display is a real OS window only the desktop app can
      // open/close — this device's own toggle button just asks the
      // sidecar to relay the request there (`extended-display.route.ts`'s
      // own doc comment); this is how it learns whether that actually
      // resulted in the window opening or closing.
      ws.on("output:timer-display-status", (message) => {
        setExtendedDisplayOpen(Boolean(message.open));
      }),
      ws.on("message:set", (message) =>
        setCurrentMessage({ text: String(message.text ?? ""), flash: Boolean(message.flash) }),
      ),
      ws.on("message:cleared", () => setCurrentMessage(null)),
      ws.on("heartbeat", (message) => {
        if (typeof message.serverTime === "number")
          skewMsRef.current = message.serverTime - Date.now();
      }),
      // Pushed directly to this one connection (never a channel broadcast —
      // see `devices.route.ts`'s own doc comment on why) whenever the
      // operator changes this device's role from the Connected Devices
      // list: a role is a claim baked into the signed token, so the only
      // way to actually change it mid-session is to swap in a replacement
      // token, which this reconnects with immediately.
      ws.on("session:role-changed", (message) => {
        onSessionUpdate({
          token: String(message.token),
          role: message.role as ControlSession["role"],
          expiresAt: Number(message.expiresAt),
        });
      }),
      ...["event:activated", "schedule:changed", "item:changed"].map((type) =>
        ws.on(type, () => void fetchEvent()),
      ),
    ];

    return () => {
      unsubscribers.forEach((unsubscribe) => unsubscribe());
      ws.stop();
    };
  }, [session.token, http, fetchEvent, reportKnownSeq, onSessionUpdate]);

  React.useEffect(() => {
    // A `clock`-type item ticks regardless of transport status — it's a
    // wall clock, not a countdown, so "paused" doesn't freeze the time.
    const ticks =
      state?.status === "running" || state?.status === "overtime" || state?.type === "clock";
    if (!ticks) return;
    const interval = setInterval(() => setNow(Date.now()), 250);
    return () => clearInterval(interval);
  }, [state?.status, state?.type]);

  const runAction = React.useCallback(async (action: () => Promise<unknown>) => {
    setActionError(null);
    setIsBusy(true);
    try {
      await action();
    } catch (cause) {
      setActionError(problemMessage(cause, "That command failed."));
    } finally {
      setIsBusy(false);
    }
  }, []);

  const dispatchTransport = React.useCallback(
    (path: string, payload: Record<string, unknown> = {}) =>
      runAction(() => http.request("POST", path, { ...payload, clientSeq: nextClientSeq() })),
    [runAction, http, nextClientSeq],
  );

  const canControl = canTransport(session.role);
  const canModerate = session.role === "operator";

  const toggleExtendedDisplay = React.useCallback(
    () => runAction(() => http.request("POST", "/api/v1/display/extended/toggle")),
    [runAction, http],
  );

  const remainingMs = state ? computeRemainingMs(state, skewMsRef.current, now) : 0;
  const elapsedMs = state ? computeElapsedMs(state, skewMsRef.current, now) : 0;
  // The two timer types with no countdown of their own: an untimed
  // count-up/session (counts up from zero, `remainingMs` is `Infinity`)
  // and a `clock` item (always shows the current time). Both mirror the
  // display-side fixes in `use-live-extended-display-data.ts`.
  const isUntimedCountUp =
    !!state && (state.type === "countup" || state.type === "session") && state.durationMs <= 0;
  const isLiveClock = state?.type === "clock";
  const currentIndex =
    event && state ? event.items.findIndex((item) => item.id === state.itemId) : -1;
  const currentItem = currentIndex >= 0 ? (event?.items[currentIndex] ?? null) : null;
  const nextItem = currentIndex >= 0 ? (event?.items[currentIndex + 1] ?? null) : null;
  const primary = resolvePrimaryAction(state?.status);
  const timerText = isLiveClock
    ? formatClockTime(now + skewMsRef.current)
    : formatDuration(isUntimedCountUp ? elapsedMs : remainingMs);
  // No target to be "near" for either no-countdown type — skip the zone
  // coloring entirely rather than let it resolve to red off a 0/`Infinity`.
  const countdownZone =
    state && !isUntimedCountUp && !isLiveClock
      ? resolveCountdownZone(state.durationMs, remainingMs, countdownColorTiers)
      : "normal";
  const countdownColor = COUNTDOWN_ZONE_COLOR[countdownZone];

  return (
    <main
      style={{
        minHeight: "100vh",
        background: "#0b0d10",
        color: "#f2f4f7",
        padding: 16,
        display: "flex",
        flexDirection: "column",
        gap: 16,
        fontFamily: "system-ui, sans-serif",
      }}
    >
      <style>{shineKeyframes}</style>
      <header
        style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
          <img
            src="/images/taskbar-icon.png"
            alt=""
            style={{ width: 28, height: 28, borderRadius: 6, flexShrink: 0 }}
          />
          <div style={{ minWidth: 0 }}>
            <h1 style={{ fontSize: 20, fontWeight: 600, margin: 0 }}>
              {event?.name ?? "StagVista Controller"}
            </h1>
            <p style={{ margin: 0, opacity: 0.6, fontSize: 12, textTransform: "capitalize" }}>
              {session.role}
            </p>
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 6, flexShrink: 0 }}>
          {canControl ? (
            <button
              type="button"
              disabled={isBusy}
              onClick={() => void toggleExtendedDisplay()}
              title={
                extendedDisplayOpen
                  ? "Extended Display is open — tap to close"
                  : "Extended Display is closed — tap to open"
              }
              style={extendedDisplayButtonStyle}
            >
              <span
                style={{
                  width: 6,
                  height: 6,
                  borderRadius: "50%",
                  background: extendedDisplayOpen ? "#22c55e" : "#6b7280",
                }}
              />
              Display
            </button>
          ) : null}
          {canModerate ? (
            <button
              type="button"
              disabled={isBusy}
              onClick={() =>
                void runAction(() =>
                  http.request("POST", "/api/v1/outputs/blackout", { on: !blackout }),
                )
              }
              style={blackoutButtonStyle(blackout)}
            >
              {blackout ? "Restore" : "Blackout"}
            </button>
          ) : null}
          <button
            type="button"
            onClick={onDisconnect}
            style={shineButtonStyle}
            className="shine-btn"
          >
            Exit
          </button>
        </div>
      </header>

      {actionError ? (
        <div style={errorBannerStyle}>
          <span>{actionError}</span>
          <button
            type="button"
            onClick={() => setActionError(null)}
            style={{
              background: "none",
              border: "none",
              color: "inherit",
              textDecoration: "underline",
              cursor: "pointer",
            }}
          >
            Dismiss
          </button>
        </div>
      ) : null}

      {blackout ? (
        <div style={{ ...errorBannerStyle, borderColor: "#f59e0b", color: "#f59e0b" }}>
          Blackout is active on all outputs.
        </div>
      ) : null}

      {currentMessage ? (
        <div
          style={{
            borderRadius: 6,
            border: "1px solid #3b82f6",
            background: "rgba(59,130,246,0.1)",
            padding: "8px 12px",
            fontSize: 13,
          }}
        >
          <strong>On screen:</strong> {currentMessage.text}
        </div>
      ) : null}

      <section style={cardStyle}>
        <p style={{ margin: 0, opacity: 0.6, fontSize: 12 }}>{currentItem?.speaker ?? " "}</p>
        <h2 style={{ margin: "4px 0", fontSize: 22 }}>{currentItem?.title ?? "No item loaded"}</h2>
        <p
          style={{
            fontSize: 48,
            fontFamily: "ui-monospace, monospace",
            fontVariantNumeric: "tabular-nums",
            margin: "8px 0",
            color: countdownColor,
            transition: "color 0.5s",
          }}
        >
          {timerText}
        </p>
        {nextItem ? (
          <p style={{ margin: 0, opacity: 0.6, fontSize: 13 }}>Next: {nextItem.title}</p>
        ) : null}
      </section>

      {canControl ? (
        <section style={{ ...cardStyle, display: "flex", flexDirection: "column", gap: 12 }}>
          <div style={{ display: "flex", gap: 8, justifyContent: "center" }}>
            <button
              type="button"
              disabled={isBusy}
              onClick={() => void dispatchTransport("/api/v1/timer/previous")}
              style={secondaryButtonStyle}
            >
              Prev
            </button>
            <button
              type="button"
              disabled={isBusy}
              onClick={() => void dispatchTransport(`/api/v1/timer/${primary.action}`)}
              style={primaryButtonStyle(isBusy)}
            >
              {primary.label}
            </button>
            <button
              type="button"
              disabled={isBusy}
              onClick={() => void dispatchTransport("/api/v1/timer/next")}
              style={secondaryButtonStyle}
            >
              Next
            </button>
          </div>
          <div style={{ display: "flex", gap: 6, justifyContent: "center" }}>
            <button
              type="button"
              disabled={isBusy}
              onClick={() => void dispatchTransport("/api/v1/timer/adjust", { deltaMs: -300_000 })}
              style={{ ...smallButtonStyle, flex: 1, height: 36 }}
            >
              -5 Min
            </button>
            <button
              type="button"
              disabled={isBusy}
              onClick={() => void dispatchTransport("/api/v1/timer/adjust", { deltaMs: -60_000 })}
              style={{ ...smallButtonStyle, flex: 1, height: 36 }}
            >
              -1 Min
            </button>
            <button
              type="button"
              disabled={isBusy}
              onClick={() => void dispatchTransport("/api/v1/timer/adjust", { deltaMs: 60_000 })}
              style={{ ...smallButtonStyle, flex: 1, height: 36 }}
            >
              +1 Min
            </button>
            <button
              type="button"
              disabled={isBusy}
              onClick={() => void dispatchTransport("/api/v1/timer/adjust", { deltaMs: 300_000 })}
              style={{ ...smallButtonStyle, flex: 1, height: 36 }}
            >
              +5 Min
            </button>
          </div>
        </section>
      ) : null}

      {canControl && event && event.items.length > 0 ? (
        <section style={cardStyle}>
          <h3 style={{ margin: "0 0 8px", fontSize: 14, opacity: 0.7 }}>Schedule</h3>
          <ul
            style={{
              listStyle: "none",
              margin: 0,
              padding: 0,
              display: "flex",
              flexDirection: "column",
              gap: 4,
              maxHeight: 280,
              overflowY: "auto",
            }}
          >
            {event.items.map((item) => (
              <li
                key={item.id}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  padding: "6px 8px",
                  borderRadius: 4,
                  background: item.id === state?.itemId ? "rgba(59,130,246,0.15)" : "transparent",
                }}
              >
                <span style={{ fontSize: 13 }}>{item.title}</span>
                <button
                  type="button"
                  disabled={isBusy}
                  onClick={() => void dispatchTransport("/api/v1/timer/jump", { itemId: item.id })}
                  style={smallButtonStyle}
                >
                  Jump
                </button>
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      {canModerate ? (
        <section style={{ ...cardStyle, display: "flex", flexDirection: "column", gap: 8 }}>
          <h3 style={{ margin: 0, fontSize: 14, opacity: 0.7 }}>Message</h3>
          <textarea
            value={messageText}
            onChange={(e) => setMessageText(e.target.value)}
            maxLength={200}
            rows={2}
            placeholder="Message to display…"
            style={{ ...pinInputStyle, height: "auto", padding: 8, fontSize: 14 }}
          />
          <label
            style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, opacity: 0.8 }}
          >
            <input
              type="checkbox"
              checked={messageFlash}
              onChange={(e) => setMessageFlash(e.target.checked)}
            />
            Blink
          </label>
          <div style={{ display: "flex", gap: 8 }}>
            <button
              type="button"
              disabled={isBusy || !messageText.trim()}
              onClick={() =>
                void runAction(async () => {
                  await http.request("POST", "/api/v1/message", {
                    text: messageText,
                    flash: messageFlash,
                  });
                  setMessageText("");
                })
              }
              style={primaryButtonStyle(isBusy || !messageText.trim())}
            >
              Send
            </button>
            <button
              type="button"
              disabled={isBusy}
              onClick={() =>
                void runAction(async () => {
                  await http.request("DELETE", "/api/v1/message");
                  setMessageText("");
                })
              }
              style={secondaryButtonStyle}
            >
              Clear
            </button>
          </div>
        </section>
      ) : null}
    </main>
  );
}

const logoIconStyle: React.CSSProperties = {
  width: 64,
  height: 64,
  borderRadius: 14,
};

const centeredPageStyle: React.CSSProperties = {
  display: "flex",
  minHeight: "100vh",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  gap: 12,
  background: "#0b0d10",
  color: "#f2f4f7",
  fontFamily: "system-ui, sans-serif",
};

const cardStyle: React.CSSProperties = {
  borderRadius: 8,
  border: "1px solid #383e46",
  background: "#14171b",
  padding: 12,
};

const errorBannerStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  borderRadius: 6,
  border: "1px solid rgba(239,68,68,0.4)",
  background: "rgba(239,68,68,0.1)",
  color: "#ef4444",
  padding: "8px 12px",
  fontSize: 13,
};

const pinInputStyle: React.CSSProperties = {
  height: 44,
  borderRadius: 6,
  border: "1px solid #383e46",
  background: "#14171b",
  color: "#f2f4f7",
  padding: "0 12px",
  fontSize: 20,
  fontVariantNumeric: "tabular-nums",
};

function primaryButtonStyle(disabled: boolean): React.CSSProperties {
  return {
    height: 44,
    borderRadius: 6,
    border: "none",
    background: "#3b82f6",
    color: "#fff",
    fontWeight: 500,
    cursor: disabled ? "not-allowed" : "pointer",
    opacity: disabled ? 0.5 : 1,
    flex: 1,
  };
}

const secondaryButtonStyle: React.CSSProperties = {
  height: 44,
  borderRadius: 6,
  border: "1px solid #383e46",
  background: "transparent",
  color: "#f2f4f7",
  fontWeight: 500,
  cursor: "pointer",
  flex: 1,
};

const smallButtonStyle: React.CSSProperties = {
  height: 28,
  borderRadius: 4,
  border: "1px solid #383e46",
  background: "transparent",
  color: "#f2f4f7",
  fontSize: 12,
  padding: "0 10px",
  cursor: "pointer",
};

const extendedDisplayButtonStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 6,
  height: 28,
  padding: "0 10px",
  borderRadius: 4,
  border: "1px solid #383e46",
  background: "transparent",
  color: "#f2f4f7",
  fontSize: 12,
  cursor: "pointer",
};

function blackoutButtonStyle(active: boolean): React.CSSProperties {
  return {
    height: 28,
    padding: "0 10px",
    borderRadius: 4,
    border: active ? "1px solid #ef4444" : "1px solid #383e46",
    background: active ? "#ef4444" : "transparent",
    color: active ? "#fff" : "#f2f4f7",
    fontSize: 12,
    fontWeight: 500,
    cursor: "pointer",
  };
}

/** Mirrors `apps/desktop`'s own yellow gradient-shine "Exit Operator View" button — same sweep technique (a `background-position` animation over a 200%-wide gradient), reimplemented as a plain `<style>` tag since this app has no Tailwind/keyframes config to hook into. */
const shineKeyframes = `
@keyframes stagvista-control-shine {
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}
.shine-btn {
  animation: stagvista-control-shine 2s linear infinite;
}
`;

const shineButtonStyle: React.CSSProperties = {
  height: 28,
  padding: "0 12px",
  borderRadius: 4,
  border: "1px solid #a16207",
  color: "#000",
  fontSize: 12,
  fontWeight: 600,
  cursor: "pointer",
  backgroundImage: "linear-gradient(110deg, #eab308 45%, #fef08a 55%, #eab308)",
  backgroundSize: "200% 100%",
};
